import { useReducer } from 'react'
import { ActionType, standardsReducer } from './standards_reducer'
import { Standard, StandardState } from 'shared/types'
import { getAPIRequest } from 'infrastructure/api'
import { doInBatches } from 'shared/utils'
import { error } from 'console'

export type IdType = number | string

export async function getStandardsForFilters(filters: StandardsFilters) {
  return standardRequest(filters)
}

export function useGetStandardsById(...standardIds: IdType[]) {
  const [{ byId, missing: existingMissing, retrieving }, dispatch] = useReducer(
    standardsReducer,
    {
      byId: {},
      missing: [],
      retrieving: [],
    },
  )
  const { standards, missing, toRetrieve } = standardIds.reduce<{
    standards: Standard[]
    missing: string[]
    toRetrieve: string[]
  }>(
    ({ standards, missing, toRetrieve }, sidt) => {
      const sid = String(sidt)
      if (sid in byId) {
        standards.push(byId[sid])
      } else if (existingMissing.includes(sid)) {
        missing.push(String(sid))
      } else if (!retrieving.includes(sid)) {
        toRetrieve.push(sid)
      }
      return { standards, missing, toRetrieve }
    },
    { standards: [], missing: [], toRetrieve: [] },
  )
  if (toRetrieve.length) {
    dispatch({ type: ActionType.Retrieving, ids: toRetrieve })
    getStandardsByIds(toRetrieve).then(({ standards, missing }) => {
      dispatch({ type: ActionType.Add, missing, standards })
    })
  }
  return { standards, missing, toRetrieve, dispatch }
}

interface StandardsFilters {
  state?: string
  standard_name?: string
  is_common_core?: boolean
  grade_id?: number
  unlimited?: boolean
}

interface FiltersWithId extends StandardsFilters {
  standard_id?: IdType
}

export function standardRequest(params: FiltersWithId) {
  const paramString = Object.entries(params)
    .filter(([, value]) => value === false || !!value)
    .map((keyVal) => keyVal.join('='))
    .join('&')
  return getAPIRequest<Standard[]>(`/api/v2/standards?${paramString}`)
}

export function getStandardStates() {
  return getAPIRequest<StandardState[]>('/api/v2/standards/states')
}

async function getStandardById(standard_id: IdType) {
  return standardRequest({ standard_id })
    .then((result) => {
      if (result.length) return result[0]
    })
    .catch(() => null)
}

export async function getStandardsByIds(standardIds: IdType[]) {
  const standards: Standard[] = []
  const missing: string[] = []
  const doStandard = async (id: IdType) => {
    const standard = await getStandardById(id)
    if (standard) {
      standards.push(standard)
    } else {
      missing.push(String(id))
    }
  }
  await doInBatches(standardIds, doStandard)
  return { standards, missing }
}

export async function mapStandardIdsToStandards(standardIds: IdType[]) {
  const standardIdToStandard: { [key: IdType]: Standard } = {}
  const standardErrors: { [key: IdType]: string } = {}
  const mapStandard = async (id: IdType) => {
    if (!standardIdToStandard[id]) {
      const standard = await getStandardById(id)
      if (standard) {
        standardIdToStandard[id] = standard
      } else {
        standardErrors[id] = `Orphaned standard ${id}`
      }
    }
  }
  await doInBatches(standardIds, mapStandard)
  return {
    standards: standardIdToStandard,
    errors: standardErrors,
  }
}
